/**
 * PersonProfile — Full home-style layout for any user's profile.
 * Renders the same 3-column layout as the home page, but:
 * - Shows the viewed person's data
 * - Hides Edit buttons when viewing someone else's profile
 * Route: /people/:userId
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { useAuth } from "@/App";
import {
  Loader2, Heart, MessageCircle, Send, MapPin, Users, Share2, Image,
  Building2, Briefcase, Star, Search, Award, Phone, Mail, Globe, Tag, GraduationCap,
  Camera, UserPlus, ChevronDown, ChevronUp, ExternalLink,
  ShoppingBag, Target, MessageSquare, Edit3,
} from "lucide-react";
import TopNav from "@/components/TopNav";
import GeoFilterBar from "@/components/GeoFilterBar";

const API = (() => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8001/api";
  return `https://api.${host}/api`;
})();

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const dummyAvatar = (name, id) => {
  const gradients = ["from-violet-500 to-purple-600", "from-emerald-500 to-teal-600", "from-blue-500 to-cyan-600", "from-rose-500 to-pink-600", "from-amber-500 to-orange-600"];
  const h = (id || name || "").length;
  return { bg: gradients[h % gradients.length], letter: (name || "?")[0].toUpperCase() };
};

// ── WISHLIST MENU (view-only, fetches the viewed person's wishes) ──
function WishlistMenuView({ onNavigate, ownerId }) {
  const [wishData, setWishData] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ownerId) return;
    setLoading(true);
    axios.get(`${API}/user/profile-wishes/${ownerId}`, { headers: getHeaders() })
      .then(res => setWishData(res.data.items || []))
      .catch(() => setWishData([]))
      .finally(() => setLoading(false));
  }, [ownerId]);

  const orgWishes = wishData.filter(w => w.wish_type === "org").slice(0, 3);
  const dealWishes = wishData.filter(w => w.wish_type === "deal").slice(0, 3);
  const peopleWishes = wishData.filter(w => w.wish_type === "people").slice(0, 3);
  const jobWishes = wishData.filter(w => w.wish_type === "job").slice(0, 3);

  const sections = [
    { key: "orgs", label: "Organisations to Connect", icon: Building2, color: "from-violet-500 to-purple-600", bg: "bg-violet-600/20", text: "text-violet-300", items: orgWishes },
    { key: "deals", label: "Deals Wishlist", icon: ShoppingBag, color: "from-amber-500 to-orange-600", bg: "bg-amber-600/20", text: "text-amber-300", items: dealWishes },
    { key: "people", label: "People to Connect", icon: Users, color: "from-emerald-500 to-teal-600", bg: "bg-emerald-600/20", text: "text-emerald-300", items: peopleWishes },
    { key: "jobs", label: "Job Opportunities", icon: Briefcase, color: "from-blue-500 to-cyan-600", bg: "bg-blue-600/20", text: "text-blue-300", items: jobWishes },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
          <Target className="w-3.5 h-3.5 text-rose-400" /> My Wishlist
        </h3>
      </div>
      {loading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-app-secondary" /></div>
      ) : (
        <div className="space-y-2">
          {sections.map(section => (
            <div key={section.key}>
              <button onClick={() => setExpanded(expanded === section.key ? null : section.key)}
                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium ${section.bg} ${section.text} transition-colors`}>
                <section.icon className="w-3.5 h-3.5" />
                <span className="flex-1 text-left">{section.label}</span>
                <span className="text-[10px] opacity-70">({section.items.length})</span>
                {expanded === section.key ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              {expanded === section.key && (
                <div className="mt-1 space-y-1 max-h-[180px] overflow-y-auto pl-3">
                  {section.items.length === 0 ? (<p className="text-[10px] text-app-secondary py-1 italic">No items listed</p>) : (
                    section.items.map((item, i) => (
                      <div key={i} className="bg-surface-item rounded-lg p-2 space-y-1">
                        <p className="text-[11px] font-medium">{item.title}</p>
                        {item.description && <p className="text-[9px] text-app-secondary line-clamp-2">{item.description}</p>}
                        <div className="flex items-center gap-2 pt-1">
                          {item.target_id && section.key === "people" && (
                            <button onClick={() => onNavigate(`/people/${item.target_id}`)}
                              className="text-[9px] text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
                              <UserPlus className="w-2.5 h-2.5" /> Follow
                            </button>
                          )}
                          {item.target_id && section.key === "orgs" && (
                            <button onClick={() => onNavigate(`/corporate-hub/${item.target_id}`)}
                              className="text-[9px] text-violet-400 hover:text-violet-300 flex items-center gap-1">
                              <ExternalLink className="w-2.5 h-2.5" /> View
                            </button>
                          )}
                          {section.key === "jobs" && (
                            <button onClick={() => onNavigate("/comms-centre/mail")}
                              className="text-[9px] text-blue-400 hover:text-blue-300 flex items-center gap-1">
                              <Mail className="w-2.5 h-2.5" /> Send Details
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── BUSINESS CARD (with conditional buttons) ──
function ProfileBusinessCard({ person, onBack, isOwner }) {
  const navigate = useNavigate();
  const avatar = dummyAvatar(person.name, person.user_id);

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-3 mb-3">
        <Avatar className="w-16 h-16 border-2 border-violet-500/30 shrink-0">
          {person.picture ? <AvatarImage src={person.picture} className="object-cover" /> : (
            <AvatarFallback className={`bg-gradient-to-br ${avatar.bg} text-xl font-bold text-white`}>
              {avatar.letter}
            </AvatarFallback>
          )}
        </Avatar>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold">{person.name || "Unknown"}</h1>
          <p className="text-xs text-app-secondary">{person.position || person.profession || ""}</p>
          <p className="text-[10px] text-app-secondary">@{person.username || "user"}</p>
        </div>
        <button onClick={onBack} className="text-app-secondary hover:text-white p-1">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs mb-3">
        <div className="space-y-1">
          <div><span className="text-[9px] text-app-secondary uppercase">Position</span><p className="text-app">{person.position || "N/A"}</p></div>
          <div><span className="text-[9px] text-app-secondary uppercase">Profession</span><p className="text-app">{person.profession || "N/A"}</p></div>
          <div><span className="text-[9px] text-app-secondary uppercase">Phone</span><p className="text-app">{person.phone || "N/A"}</p></div>
          <div><span className="text-[9px] text-app-secondary uppercase">Email</span><p className="text-app">{person.email || "N/A"}</p></div>
        </div>
        <div className="space-y-1">
          <div><span className="text-[9px] text-app-secondary uppercase">From</span><p className="text-app">{person.country || "N/A"}</p></div>
          <div><span className="text-[9px] text-app-secondary uppercase">Location</span><p className="text-app">{person.city || person.country || "N/A"}</p></div>
          <div>
            <h4 className="text-[9px] uppercase tracking-wider text-app-secondary mb-0.5">Skills</h4>
            <div className="flex flex-wrap gap-1">{person.skills && Array.isArray(person.skills) && person.skills.length > 0 ? person.skills.map((s, i) => (<Badge key={i} className="bg-emerald-600/20 text-emerald-400 text-[8px] px-1.5 py-0.5">{s}</Badge>)) : (<span className="text-[10px] text-app-secondary">None</span>)}</div>
          </div>
          <div>
            <h4 className="text-[9px] uppercase tracking-wider text-app-secondary mb-0.5">Services I Can Offer</h4>
            <div className="flex flex-wrap gap-1">{person.services && Array.isArray(person.services) && person.services.length > 0 ? person.services.map((s, i) => (<Badge key={i} className="bg-amber-600/20 text-amber-400 text-[8px] px-1.5 py-0.5">{s}</Badge>)) : (<span className="text-[10px] text-app-secondary">None</span>)}</div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 pt-3 border-t border-white/10">
        {isOwner ? (
          <button onClick={() => navigate("/")}
            className="flex-1 bg-violet-600 hover:bg-violet-500 text-white text-[10px] font-medium py-2 rounded-lg flex items-center justify-center gap-1.5">
            <Edit3 className="w-3 h-3" /> Edit My Profile
          </button>
        ) : (
          <>
            <button onClick={() => window.location.href="/comms-centre/mail/inbox"}
              className="flex-1 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-medium py-1.5 rounded-lg flex items-center justify-center gap-1.5">
              <MailIcon className="w-3 h-3" /> Request Contact
            </button>
            <button onClick={() => window.location.href="/comms-centre/chat"}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-medium py-1.5 rounded-lg flex items-center justify-center gap-1.5">
              <Share2Icon className="w-3 h-3" /> Share Contact
            </button>
            <button onClick={() => window.location.href="/comms-centre/chat"}
              className="flex-1 bg-amber-600 hover:bg-amber-500 text-white text-[10px] font-medium py-1.5 rounded-lg flex items-center justify-center gap-1.5">
              <UserPlus className="w-3 h-3" /> Follow
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function MailIcon(props) {
  return <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 4l-10 8L2 4"/></svg>;
}

function Share2Icon(props) {
  return <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>;
}

// ── Corporate Sidebar ──
function CorporateSidebar({ onNavigate }) {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [csCountry, setCsCountry] = useState("all");
  const [csRegion, setCsRegion] = useState("all");
  const [csOrgType, setCsOrgType] = useState("all");
  const [csCategory, setCsCategory] = useState("all");
  const [csRadius, setCsRadius] = useState("");
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/v2/orgs`, { headers: getHeaders() })
      .then(res => setOrgs(res.data.items || [])).catch(() => setOrgs([])).finally(() => setLoading(false));
  }, []);
  const filtered = orgs.filter(o => {
    if (search && !(o.name || "").toLowerCase().includes(search.toLowerCase())) return false;
    if (csCountry !== "all" && o.country !== csCountry) return false;
    if (csOrgType !== "all" && o.org_type !== csOrgType) return false;
    if (csCategory !== "all" && o.category !== csCategory) return false;
    return true;
  });
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-violet-400" /> Corporate Hub</h3>
        <button onClick={() => onNavigate("/corporate-hub")} className="text-xs text-violet-400 hover:text-violet-300 font-medium">View All</button>
      </div>
      <GeoFilterBar country={csCountry} onCountryChange={setCsCountry} region={csRegion} onRegionChange={setCsRegion} radius={csRadius} onRadiusChange={setCsRadius} />
      <div className="flex gap-1 mb-2 overflow-x-auto scrollbar-dark">
        <button onClick={() => setCsOrgType("all")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csOrgType === "all" ? "bg-violet-600/30 text-violet-300 font-semibold" : "bg-surface-item text-app-secondary"}`}>All</button>
        {["government","ngo","business"].map(t => (
          <button key={t} onClick={() => setCsOrgType(t)} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csOrgType === t ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>{t}</button>
        ))}
      </div>      <div className="flex gap-1 mb-1 overflow-x-auto scrollbar-dark">
        <button onClick={() => setCsCategory("all")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "all" ? "bg-violet-600/30 text-violet-300 font-semibold" : "bg-surface-item text-app-secondary"}`}>All</button>
        <button key="National" onClick={() => setCsCategory("National")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "National" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>National</button>
        <button key="Provincial" onClick={() => setCsCategory("Provincial")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Provincial" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Provincial</button>
        <button key="Municipal" onClick={() => setCsCategory("Municipal")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Municipal" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Municipal</button>
        <button key="State-Owned Entity" onClick={() => setCsCategory("State-Owned Entity")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "State-Owned Entity" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>State-Owned Entity</button>
        <button key="Regulatory" onClick={() => setCsCategory("Regulatory")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Regulatory" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Regulatory</button>
        <button key="Human Rights" onClick={() => setCsCategory("Human Rights")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Human Rights" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Human Rights</button>
        <button key="Education" onClick={() => setCsCategory("Education")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Education" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Education</button>
        <button key="Health" onClick={() => setCsCategory("Health")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Health" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Health</button>
        <button key="Environment" onClick={() => setCsCategory("Environment")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Environment" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Environment</button>
        <button key="Community" onClick={() => setCsCategory("Community")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Community" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Community</button>
        <button key="Relief" onClick={() => setCsCategory("Relief")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Relief" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Relief</button>
        <button key="Technology" onClick={() => setCsCategory("Technology")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Technology" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Technology</button>
        <button key="Finance" onClick={() => setCsCategory("Finance")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Finance" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Finance</button>
        <button key="Manufacturing" onClick={() => setCsCategory("Manufacturing")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Manufacturing" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Manufacturing</button>
        <button key="Retail" onClick={() => setCsCategory("Retail")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Retail" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Retail</button>
        <button key="Healthcare" onClick={() => setCsCategory("Healthcare")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Healthcare" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Healthcare</button>
        <button key="Energy" onClick={() => setCsCategory("Energy")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Energy" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Energy</button>
        <button key="Agriculture" onClick={() => setCsCategory("Agriculture")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Agriculture" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Agriculture</button>
        <button key="Logistics" onClick={() => setCsCategory("Logistics")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Logistics" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Logistics</button>
        <button key="Tourism" onClick={() => setCsCategory("Tourism")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Tourism" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Tourism</button>
      </div>

      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-secondary" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
          className="w-full bg-surface-item border border-input rounded-lg pl-8 pr-3 py-1.5 text-xs text-app placeholder:text-app-secondary focus:outline-none focus:border-violet-500" />
      </div>
      <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
        {loading ? (<div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-app-secondary" /></div>) : filtered.length === 0 ? (<p className="text-xs text-app-secondary text-center py-4">No organisations found</p>) : (
          filtered.slice(0, 6).map(org => (
            <div key={org.id || org._id} onClick={() => onNavigate(`/corporate-hub/${org.id || org._id}`)}
              className="bg-surface-item rounded-lg p-2 cursor-pointer hover:bg-surface-hover transition-colors">
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold shrink-0">{dummyAvatar(org.name).letter}</div>
                <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate text-app">{org.name}</p><p className="text-[10px] text-app-secondary">{org.country || ""}</p></div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── People Sidebar ──
function PeopleSidebar({ onNavigate }) {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [psCountry, setPsCountry] = useState("all");
  const [psRegion, setPsRegion] = useState("all");
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/v2/people`, { headers: getHeaders() })
      .then(res => setPeople(res.data.items || [])).catch(() => setPeople([])).finally(() => setLoading(false));
  }, []);
  const filtered = people.filter(p => {
    if (search && !(p.name || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-emerald-400" /> My People</h3>
        <button onClick={() => onNavigate("/people-directory")} className="text-xs text-emerald-400 hover:text-emerald-300 font-medium">View All</button>
      </div>
      <GeoFilterBar country={psCountry} onCountryChange={setPsCountry} region={psRegion} onRegionChange={setPsRegion} />
      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-secondary" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
          className="w-full bg-surface-item border border-input rounded-lg pl-8 pr-3 py-1.5 text-xs text-app placeholder:text-app-secondary focus:outline-none focus:border-emerald-500" />
      </div>
      <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
        {loading ? (<div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-app-secondary" /></div>) : filtered.length === 0 ? (<p className="text-xs text-app-secondary text-center py-4">No people found</p>) : (
          filtered.slice(0, 6).map(person => (
            <div key={person.user_id || person._id} onClick={() => onNavigate(`/people/${person.user_id}`)}
              className="bg-surface-item rounded-lg p-2 cursor-pointer hover:bg-surface-hover transition-colors">
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-xs font-bold shrink-0">{dummyAvatar(person.name, person.user_id).letter}</div>
                <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate text-app">{person.name}</p><p className="text-[10px] text-app-secondary">{person.position || person.country || ""}</p></div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── Activity Feed (for the viewed person) ──
function PersonActivityFeed({ person }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [likedPosts, setLikedPosts] = useState({});
  const [reactions, setReactions] = useState({});
  const [shares, setShares] = useState({});
  const [showReactionPicker, setShowReactionPicker] = useState({});
  const [postImage, setPostImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [commentInputs, setCommentInputs] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [commentsData, setCommentsData] = useState({});

  useEffect(() => {
    fetchPosts();
  }, [person]);

  const fetchPosts = async () => {
    if (!person?.user_id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/activity/${person.user_id}`, { headers: getHeaders() });
      const items = res.data.items || [];
      setPosts(items);
      items.forEach(p => {
        fetchReactions(p.id);
        setShares(prev => ({ ...prev, [p.id]: p.shares || 0 }));
      });
    } catch { setPosts([]); }
    setLoading(false);
  };

  const handleLike = async (postId) => {
    try {
      const res = await axios.post(`${API}/activity/${postId}/like`, {}, { headers: getHeaders() });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes: p.likes + (res.data.status === "liked" ? 1 : -1) } : p));
      setLikedPosts(prev => ({ ...prev, [postId]: res.data.status === "liked" }));
    } catch {}
  };

  const handleReact = async (postId, reaction) => {
    try {
      const res = await axios.post(`${API}/activity/${postId}/react`, { reaction }, { headers: getHeaders() });
      setShowReactionPicker(prev => ({ ...prev, [postId]: false }));
      if (res.data.status === "reacted") {
        setReactions(prev => ({ ...prev, [postId]: { ...prev[postId], [reaction]: (prev[postId]?.[reaction] || 0) + 1 } }));
      }
    } catch {}
  };

  const handleShare = async (postId) => {
    try {
      const res = await axios.post(`${API}/activity/${postId}/share`, {}, { headers: getHeaders() });
      setShares(prev => ({ ...prev, [postId]: res.data.shares }));
      toast.success("Post shared!");
    } catch { toast.error("Failed to share"); }
  };

  const fetchReactions = async (postId) => {
    try {
      const res = await axios.get(`${API}/activity/${postId}/reactions`, { headers: getHeaders() });
      setReactions(prev => ({ ...prev, [postId]: res.data.reactions }));
    } catch {}
  };

  const totalReactions = (postId) => {
    const r = reactions[postId] || {};
    return Object.values(r).reduce((a, b) => a + b, 0);
  };

  const REACTION_EMOJIS = { "like": "👍", "love": "❤️", "celebrate": "🎉", "support": "💪", "insightful": "💡" };
  const REACTION_COLORS = { "like": "text-blue-400", "love": "text-red-400", "celebrate": "text-amber-400", "support": "text-emerald-400", "insightful": "text-purple-400" };

  const fetchComments = async (postId) => {
    try {
      const res = await axios.get(`${API}/activity/${postId}/comments`, { headers: getHeaders() });
      setCommentsData(prev => ({ ...prev, [postId]: res.data.items || [] }));
      setExpandedComments(prev => ({ ...prev, [postId]: true }));
    } catch {}
  };

  const handleComment = async (postId) => {
    const text = commentInputs[postId];
    if (!text?.trim()) return;
    try {
      const res = await axios.post(`${API}/activity/${postId}/comments`, { content: text }, { headers: getHeaders() });
      setCommentsData(prev => ({ ...prev, [postId]: [...(prev[postId] || []), res.data] }));
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments_count: (p.comments_count || 0) + 1 } : p));
      setCommentInputs(prev => ({ ...prev, [postId]: "" }));
    } catch { toast.error("Failed to comment"); }
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = Math.floor((new Date() - d) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-app-secondary" /></div>
      ) : posts.length === 0 ? (
        <Card className="bg-app-card border-white/10 p-6 text-center"><p className="text-sm text-app-secondary">No posts yet.</p></Card>
      ) : (
        posts.slice(0, 20).map(post => (
          <Card key={post.id} className="bg-app-card border-white/10 p-4">
            <div className="flex items-center gap-3 mb-3">
              <Avatar className="w-9 h-9"><AvatarFallback className="bg-zinc-700 text-xs text-white">{(post.author_name || "U")[0]}</AvatarFallback></Avatar>
              <div><p className="text-sm font-medium">{post.author_name || "User"}</p><p className="text-[10px] text-app-secondary">{formatTime(post.created_at)}</p></div>
            </div>
            <p className="text-sm text-app mb-3 whitespace-pre-wrap">{post.content}</p>
            <div className="flex items-center gap-3 text-xs text-app-secondary border-t border-white/10 pt-3 flex-wrap">
              <div className="relative">
                <button onClick={() => setShowReactionPicker(prev => ({ ...prev, [post.id]: !prev[post.id] }))}
                  className="flex items-center gap-1 hover:text-amber-400 transition">
                  <Heart className={`w-4 h-4 ${totalReactions(post.id) > 0 ? "fill-red-400 text-red-400" : ""}`} />
                  {totalReactions(post.id) || 0}
                </button>
                {showReactionPicker[post.id] && (
                  <div className="absolute bottom-full left-0 mb-1 flex gap-1 bg-zinc-800 border border-input rounded-lg px-2 py-1.5 shadow-xl z-10">
                    {Object.entries(REACTION_EMOJIS).map(([key, emoji]) => (
                      <button key={key} onClick={() => handleReact(post.id, key)}
                        className="hover:scale-125 transition-transform text-sm p-0.5" title={key}>{emoji}</button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => handleReact(post.id, "like")}
                className={`flex items-center gap-1 hover:text-red-400 transition ${likedPosts[post.id] ? "text-red-400" : ""}`}>
                <Heart className={`w-4 h-4 ${likedPosts[post.id] ? "fill-red-400" : ""}`} />{post.likes || 0}
              </button>
              <button onClick={() => handleShare(post.id)}
                className="flex items-center gap-1 hover:text-emerald-400 transition">
                <Share2 className="w-4 h-4" />{shares[post.id] || 0}
              </button>
              <button onClick={() => { expandedComments[post.id] ? setExpandedComments(prev => ({ ...prev, [post.id]: false })) : fetchComments(post.id); }}
                className="flex items-center gap-1 hover:text-blue-400 transition">
                <MessageCircle className="w-4 h-4" />{post.comments_count || 0}
              </button>
            </div>
            {totalReactions(post.id) > 0 && (
              <div className="flex items-center gap-1.5 pt-1 text-xs">
                {Object.entries(REACTION_EMOJIS).map(([key, emoji]) => {
                  const count = reactions[post.id]?.[key] || 0;
                  if (count === 0) return null;
                  return (<span key={key} className="flex items-center gap-0.5 text-app-secondary"><span>{emoji}</span><span className="text-[10px]">{count}</span></span>);
                })}
              </div>
            )}
            {post.image_url && (
              <div className="mt-2 rounded-lg overflow-hidden">
                <img src={post.image_url} alt="Post attachment" className="w-full max-h-64 object-cover rounded-lg" onError={e => e.target.style.display = "none"} />
              </div>
            )}
            {expandedComments[post.id] && (
              <div className="mt-3 pt-3 border-t border-white/10 space-y-3">
                {commentsData[post.id]?.map(c => (
                  <div key={c.id} className="flex items-start gap-2">
                    <Avatar className="w-6 h-6 shrink-0"><AvatarFallback className="bg-zinc-700 text-[10px] text-white">{(c.author_name || "U")[0]}</AvatarFallback></Avatar>
                    <div className="flex-1 bg-surface-item rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2"><span className="text-xs font-medium">{c.author_name}</span><span className="text-[10px] text-app-secondary">{formatTime(c.created_at)}</span></div>
                      <p className="text-xs text-app mt-0.5">{c.content}</p>
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <input type="text" value={commentInputs[post.id] || ""}
                    onChange={e => setCommentInputs(prev => ({ ...prev, [post.id]: e.target.value }))}
                    onKeyDown={e => e.key === "Enter" && handleComment(post.id)}
                    placeholder="Write a comment..."
                    className="flex-1 bg-surface-item border border-input rounded-lg px-3 py-1.5 text-sm text-app placeholder:text-app-secondary focus:outline-none focus:border-violet-500" />
                  <button onClick={() => handleComment(post.id)} className="text-violet-400 hover:text-violet-300"><Send className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </Card>
        ))
      )}
    </div>
  );
}

// ── MAIN EXPORT ──


export default function PersonProfile() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const auth = useAuth();
  const isOwner = auth.user?.user_id === userId;
  const [person, setPerson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMobileSidebars, setShowMobileSidebars] = useState(window.innerWidth >= 1024);

    useEffect(() => {
    const handleResize = () => { if (window.innerWidth >= 1024) setShowMobileSidebars(true); };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

useEffect(() => {
    if (!userId) { setLoading(false); setError("No user ID"); return; }
    setLoading(true);
    setError(null);
    axios.get(`${API}/v2/people/${userId}`, { headers: getHeaders() })
      .then(res => { if (res.data?.error) { setError(res.data.error); setPerson(null); } else { setPerson(res.data); } })
      .catch(err => { setError(err.message || "Failed to load profile"); setPerson(null); })
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-app text-app">
        <TopNav />
        <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-6 h-6 animate-spin text-app-secondary" /></div>
      </div>
    );
  }

  if (error || !person) {
    return (
      <div className="min-h-screen bg-app text-app">
        <TopNav />
        <div className="max-w-4xl mx-auto px-6 py-8">
          <Card className="bg-app-card border-white/10 p-8 text-center">
            <Users className="w-12 h-12 text-app-secondary mx-auto mb-3" />
            <p className="text-app-secondary mb-4">{error || "Could not load profile"}</p>
            <Button onClick={() => navigate("/people-directory")} variant="outline">Back to People Hub</Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app text-app">
      <TopNav />
      <div className="max-w-7xl mx-auto px-3 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

          {/* TOP ROW: Business Card (9 cols) + Wishlist (3 cols) */}
          <div className="lg:col-span-9">
            <Card className="bg-app-card border-white/10 p-3">
              <ProfileBusinessCard person={person} onBack={() => navigate("/people-directory")} isOwner={isOwner} />
            </Card>
          </div>

          <div className="lg:col-span-3">
            <Card className="bg-app-card border-white/10 p-3">
              <WishlistMenuView onNavigate={navigate} ownerId={person.user_id} />
            </Card>
          </div>

          {/* Mobile sidebar toggle */}
          <div className="lg:hidden col-span-full flex gap-2 mb-2">
            <button onClick={() => setShowMobileSidebars(!showMobileSidebars)}
              className="flex-1 bg-surface-item hover:bg-surface-hover text-zinc-300 text-xs py-2 rounded-lg font-medium transition-colors">
              {showMobileSidebars ? "Hide Sidebars ▲" : "Show Sidebars ▼"}
            </button>
          </div>

          {/* LEFT COLUMN: Corporate Hub + People (hidden on mobile unless toggled) */}
          {showMobileSidebars && (
          <div className="lg:col-span-3 space-y-3">
            <Card className="bg-app-card border-white/10 p-3">
              <CorporateSidebar onNavigate={navigate} />
            </Card>
            <Card className="bg-app-card border-white/10 p-3">
              <PeopleSidebar onNavigate={navigate} />
            </Card>
          </div>
          )}

          {/* CENTER: Activity Feed */}
          <div className="lg:col-span-6 space-y-4">
            <PersonActivityFeed person={person} />
          </div>

          {/* RIGHT: Deals + Wishes (hidden on mobile unless toggled) */}
          {showMobileSidebars && (
          <div className="lg:col-span-3 space-y-3">
            <Card className="bg-app-card border-white/10 p-3">
              <MiniDealsSidebar onNavigate={navigate} />
            </Card>
            <Card className="bg-app-card border-white/10 p-3">
              <MiniWishesSidebar onNavigate={navigate} />
            </Card>
          </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ── Mini Deals Sidebar ──
function MiniDealsSidebar({ onNavigate }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dCountry, setDCountry] = useState("all");
  const [dRegion, setDRegion] = useState("all");
  const [dRadius, setDRadius] = useState("");
  const [dCategory, setDCategory] = useState("all");
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/deals/list`, { headers: getHeaders() })
      .then(res => setDeals((res.data.items || []).filter(d => d.status === "active")))
      .catch(() => setDeals([])).finally(() => setLoading(false));
  }, []);
  return (
    <div><div className="flex items-center justify-between mb-2"><h3 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5"><Tag className="w-3.5 h-3.5 text-amber-400" /> Deals</h3><button onClick={() => onNavigate("/deals")} className="text-xs text-amber-400 hover:text-amber-300 font-medium">View All</button></div>
    <GeoFilterBar country={dCountry} onCountryChange={setDCountry} region={dRegion} onRegionChange={setDRegion} radius={dRadius} onRadiusChange={setDRadius} />
      <div className="flex gap-1 mb-2 mt-2 overflow-x-auto scrollbar-dark">
        <button onClick={() => setDCategory("all")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${dCategory === "all" ? "bg-amber-600/30 text-amber-300" : "bg-surface-item text-app-secondary"}`}>All</button>
        <button key="Food & Drink" onClick={() => setDCategory("Food & Drink")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${dCategory === "Food & Drink" ? "bg-amber-600/30 text-amber-300" : "bg-surface-item text-app-secondary"}`}>Food & Drink</button>
        <button key="Health & Beauty" onClick={() => setDCategory("Health & Beauty")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${dCategory === "Health & Beauty" ? "bg-amber-600/30 text-amber-300" : "bg-surface-item text-app-secondary"}`}>Health & Beauty</button>
        <button key="Travel & Hotels" onClick={() => setDCategory("Travel & Hotels")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${dCategory === "Travel & Hotels" ? "bg-amber-600/30 text-amber-300" : "bg-surface-item text-app-secondary"}`}>Travel & Hotels</button>
        <button key="Electronics" onClick={() => setDCategory("Electronics")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${dCategory === "Electronics" ? "bg-amber-600/30 text-amber-300" : "bg-surface-item text-app-secondary"}`}>Electronics</button>
        <button key="Fashion" onClick={() => setDCategory("Fashion")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${dCategory === "Fashion" ? "bg-amber-600/30 text-amber-300" : "bg-surface-item text-app-secondary"}`}>Fashion</button>
        <button key="Home & Garden" onClick={() => setDCategory("Home & Garden")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${dCategory === "Home & Garden" ? "bg-amber-600/30 text-amber-300" : "bg-surface-item text-app-secondary"}`}>Home & Garden</button>
        <button key="Automotive" onClick={() => setDCategory("Automotive")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${dCategory === "Automotive" ? "bg-amber-600/30 text-amber-300" : "bg-surface-item text-app-secondary"}`}>Automotive</button>
        <button key="Education" onClick={() => setDCategory("Education")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${dCategory === "Education" ? "bg-amber-600/30 text-amber-300" : "bg-surface-item text-app-secondary"}`}>Education</button>
        <button key="Services" onClick={() => setDCategory("Services")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${dCategory === "Services" ? "bg-amber-600/30 text-amber-300" : "bg-surface-item text-app-secondary"}`}>Services</button>
        <button key="Entertainment" onClick={() => setDCategory("Entertainment")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${dCategory === "Entertainment" ? "bg-amber-600/30 text-amber-300" : "bg-surface-item text-app-secondary"}`}>Entertainment</button>
        <button key="Groceries" onClick={() => setDCategory("Groceries")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${dCategory === "Groceries" ? "bg-amber-600/30 text-amber-300" : "bg-surface-item text-app-secondary"}`}>Groceries</button>
        <button key="Other" onClick={() => setDCategory("Other")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${dCategory === "Other" ? "bg-amber-600/30 text-amber-300" : "bg-surface-item text-app-secondary"}`}>Other</button>
      </div>
    {loading ? (<div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-app-secondary" /></div>) : deals.length === 0 ? (<p className="text-xs text-app-secondary text-center py-4">No deals</p>) : (
      <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
        {deals.slice(0, 5).map(deal => (
          <div key={deal.id} onClick={() => onNavigate("/deals")} className="bg-surface-item rounded-lg p-2 cursor-pointer hover:bg-surface-hover transition-colors">
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-600 to-orange-600 flex items-center justify-center text-white text-xs font-bold shrink-0"><Tag className="w-4 h-4" /></div>
              <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate text-app">{deal.title}</p><span className="text-xs font-bold text-amber-400">R{deal.deal_price}</span></div>
            </div>
          </div>
        ))}
      </div>
    )}
    </div>
  );
}

// ── Mini Wishes Sidebar ──
function MiniWishesSidebar({ onNavigate }) {
  const [wishes, setWishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wCountry, setWCountry] = useState("all");
  const [wRegion, setWRegion] = useState("all");
  const [wRadius, setWRadius] = useState("");
  const [wCategory, setWCategory] = useState("all");
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/wishes/list`, { headers: getHeaders() })
      .then(res => setWishes(res.data.items || [])).catch(() => setWishes([])).finally(() => setLoading(false));
  }, []);
  return (
    <div className="mt-3"><div className="flex items-center justify-between mb-2"><h3 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-pink-400" /> Wishes</h3><button onClick={() => onNavigate("/wish-centre")} className="text-xs text-pink-400 hover:text-pink-300 font-medium">View All</button></div>
    <GeoFilterBar country={wCountry} onCountryChange={setWCountry} region={wRegion} onRegionChange={setWRegion} radius={wRadius} onRadiusChange={setWRadius} />
      <div className="flex gap-1 mb-2 mt-2 overflow-x-auto scrollbar-dark">
        <button onClick={() => setWCategory("all")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wCategory === "all" ? "bg-pink-600/30 text-pink-300" : "bg-surface-item text-app-secondary"}`}>All</button>
        <button key="Services" onClick={() => setWCategory("Services")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wCategory === "Services" ? "bg-pink-600/30 text-pink-300" : "bg-surface-item text-app-secondary"}`}>Services</button>
        <button key="Products" onClick={() => setWCategory("Products")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wCategory === "Products" ? "bg-pink-600/30 text-pink-300" : "bg-surface-item text-app-secondary"}`}>Products</button>
        <button key="Jobs" onClick={() => setWCategory("Jobs")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wCategory === "Jobs" ? "bg-pink-600/30 text-pink-300" : "bg-surface-item text-app-secondary"}`}>Jobs</button>
        <button key="Learning" onClick={() => setWCategory("Learning")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wCategory === "Learning" ? "bg-pink-600/30 text-pink-300" : "bg-surface-item text-app-secondary"}`}>Learning</button>
        <button key="Health" onClick={() => setWCategory("Health")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wCategory === "Health" ? "bg-pink-600/30 text-pink-300" : "bg-surface-item text-app-secondary"}`}>Health</button>
        <button key="Travel" onClick={() => setWCategory("Travel")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wCategory === "Travel" ? "bg-pink-600/30 text-pink-300" : "bg-surface-item text-app-secondary"}`}>Travel</button>
        <button key="Technology" onClick={() => setWCategory("Technology")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wCategory === "Technology" ? "bg-pink-600/30 text-pink-300" : "bg-surface-item text-app-secondary"}`}>Technology</button>
        <button key="Home" onClick={() => setWCategory("Home")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wCategory === "Home" ? "bg-pink-600/30 text-pink-300" : "bg-surface-item text-app-secondary"}`}>Home</button>
        <button key="Fashion" onClick={() => setWCategory("Fashion")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wCategory === "Fashion" ? "bg-pink-600/30 text-pink-300" : "bg-surface-item text-app-secondary"}`}>Fashion</button>
        <button key="Food" onClick={() => setWCategory("Food")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wCategory === "Food" ? "bg-pink-600/30 text-pink-300" : "bg-surface-item text-app-secondary"}`}>Food</button>
        <button key="Entertainment" onClick={() => setWCategory("Entertainment")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wCategory === "Entertainment" ? "bg-pink-600/30 text-pink-300" : "bg-surface-item text-app-secondary"}`}>Entertainment</button>
        <button key="Volunteering" onClick={() => setWCategory("Volunteering")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wCategory === "Volunteering" ? "bg-pink-600/30 text-pink-300" : "bg-surface-item text-app-secondary"}`}>Volunteering</button>
        <button key="Mentorship" onClick={() => setWCategory("Mentorship")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wCategory === "Mentorship" ? "bg-pink-600/30 text-pink-300" : "bg-surface-item text-app-secondary"}`}>Mentorship</button>
        <button key="Funding" onClick={() => setWCategory("Funding")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wCategory === "Funding" ? "bg-pink-600/30 text-pink-300" : "bg-surface-item text-app-secondary"}`}>Funding</button>
        <button key="Other" onClick={() => setWCategory("Other")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wCategory === "Other" ? "bg-pink-600/30 text-pink-300" : "bg-surface-item text-app-secondary"}`}>Other</button>
      </div>
    {loading ? (<div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-app-secondary" /></div>) : wishes.length === 0 ? (<p className="text-xs text-app-secondary text-center py-4">No wishes</p>) : (
      <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
        {wishes.slice(0, 5).map(wish => (
          <div key={wish.id} onClick={() => onNavigate("/wish-centre")} className="bg-surface-item rounded-lg p-2 cursor-pointer hover:bg-surface-hover transition-colors">
            <div className="flex items-start gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white text-xs font-bold shrink-0"><Star className="w-4 h-4" /></div>
              <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate text-app">{wish.title}</p><p className="text-[10px] text-app-secondary">{wish.category}</p></div>
            </div>
          </div>
        ))}
      </div>
    )}
    </div>
  );
}
